/**
 * Ticket Lifecycle v2 — Advisor Delay Impact display-state helper
 * (Slice 5A.6-lite UI).
 *
 * Pure decision helper that consolidates the bucket-aware rules for
 * rendering "Advisor Delay" on `TicketLifecycleTimeCard`. Keeping the
 * rules out of the component makes them unit-testable without DOM and
 * lets the component stay presentational.
 *
 * Scope: **read-only display state only**. Does NOT mutate anything.
 * Does NOT feed Tech/Bay OptiTime, Chronomancer, or any scheduling
 * surface. Does NOT change advisor metrics aggregation. The component
 * mounting this helper is flag-gated by `custodyHandoff` (no new flag).
 *
 * Inputs:
 *   - `advisorObservedMinutes` from `deriveTimeBuckets` (slice 5A.1).
 *   - `jobMix` from `classifyTicketJobMix` (slice 5A.5).
 *   - `remainingApprovedWork` from `computeRemainingApprovedWorkMinutes`
 *     (slice 5A.3a) — may legitimately be `null` (unknown).
 *   - `businessState` from `useTicketBusinessState` — the approval gate.
 *
 * Decision tree (first matching rule wins):
 *   1. `advisorObservedMinutes <= 0`  → hide the tile (`shouldRender:false`).
 *      Reason: no handoff time means there is nothing to attribute.
 *   2. No `jobMix`                    → render '—' + "Job mix unavailable …".
 *   3. No `businessState`             → render '—' + "Approval state unavailable …".
 *   4. `businessState` < `approved`   → render '—' + "Advisor delay is calculated after the ticket reaches approved/work status." (Slice 5C.1 copy).
 *      The full set of approved-or-later states is locked in
 *      `APPROVED_OR_LATER_STATES`.
 *   5. Bucket-specific:
 *        - `work_with_dependency` → '—' + "Hold/dependency tracking not available yet."
 *        - `diag_plus_work`       → if `remainingApprovedWork` is null,
 *          '—' + "Remaining approved work unavailable; impact not calculated.";
 *          otherwise overlap-adjusted value with the standard note.
 *        - `diag_only`            → full advisor-observed value.
 *        - `comeback_recheck`     → 0.6-weighted value.
 *        - `work_only`            → 0 (helper confidently returns 0).
 *
 * Boundary: this module is analytics-only / display-state only. It must
 * not be imported into Chronomancer / scheduling / timer / parking /
 * rollover / bay / invariants hot paths. The drift detector boundary
 * guard pins this.
 */

import type { TicketBusinessState } from '@/lib/tickets/businessStateMachine';
import { calculateAdvisorDelayImpact } from './advisorDelayImpact';
import type {
  JobMixClassification,
  TicketJobMixLabel,
} from './jobMixClassifier';
import type { RemainingApprovedWorkResult } from './remainingApprovedWork';

/**
 * Business states at or past `approved`. Mirrors the canonical chain
 * in `src/lib/tickets/businessStateMachine.ts`:
 *   draft → estimating → awaiting_approval → approved → in_progress →
 *   work_complete → ready_to_invoice → invoiced → paid → closed.
 * `voided` is intentionally excluded (terminal, not an approved path).
 */
export const APPROVED_OR_LATER_STATES: ReadonlyArray<TicketBusinessState> = [
  'approved',
  'in_progress',
  'work_complete',
  'ready_to_invoice',
  'invoiced',
  'paid',
  'closed',
];

export type AdvisorDelayImpactDisplayReason =
  | 'value'
  | 'no_handoff'
  | 'classification_missing'
  | 'business_state_missing'
  | 'not_approved'
  | 'overlap_unavailable'
  | 'dependency_deferred';

export interface AdvisorDelayImpactDisplayInput {
  advisorObservedMinutes: number;
  jobMix: JobMixClassification | null;
  /** May be `null` either because the helper hasn't been called yet, or
   *  because it refused to compute (`method: 'insufficient_data'`). */
  remainingApprovedWork: RemainingApprovedWorkResult | null;
  /** `null`/`undefined` means we don't yet have a reliable business
   *  state read — the helper treats both the same way. */
  businessState: TicketBusinessState | string | null | undefined;
}

export interface AdvisorDelayImpactDisplayState {
  /** When `false`, callers should hide the tile entirely. */
  shouldRender: boolean;
  /** User-facing job-mix label, or `null` when no bucket is rendered. */
  bucketLabel: TicketJobMixLabel | null;
  /** Minutes to render; `null` ⇒ render `'—'`. */
  valueMinutes: number | null;
  /** Human-readable note below the value. */
  note: string;
  /** Machine-friendly reason for tests. */
  reason: AdvisorDelayImpactDisplayReason;
}

function isApprovedState(
  state: TicketBusinessState | string | null | undefined,
): boolean {
  if (!state) return false;
  return (APPROVED_OR_LATER_STATES as ReadonlyArray<string>).includes(
    state as string,
  );
}

export function buildAdvisorDelayImpactDisplayState(
  input: AdvisorDelayImpactDisplayInput,
): AdvisorDelayImpactDisplayState {
  if (input.advisorObservedMinutes <= 0) {
    return {
      shouldRender: false,
      bucketLabel: null,
      valueMinutes: null,
      note: 'No advisor handoff time yet.',
      reason: 'no_handoff',
    };
  }

  if (!input.jobMix) {
    return {
      shouldRender: true,
      bucketLabel: null,
      valueMinutes: null,
      note: 'Job mix unavailable; advisor delay impact not calculated.',
      reason: 'classification_missing',
    };
  }

  if (input.businessState === null || input.businessState === undefined) {
    return {
      shouldRender: true,
      bucketLabel: input.jobMix.label,
      valueMinutes: null,
      note: 'Approval state unavailable; delay impact not calculated.',
      reason: 'business_state_missing',
    };
  }

  if (!isApprovedState(input.businessState)) {
    return {
      shouldRender: true,
      bucketLabel: input.jobMix.label,
      valueMinutes: null,
      note: 'Advisor delay is calculated after the ticket reaches approved/work status.',
      reason: 'not_approved',
    };
  }

  const bucket = input.jobMix.bucket;

  if (bucket === 'work_with_dependency') {
    return {
      shouldRender: true,
      bucketLabel: input.jobMix.label,
      valueMinutes: null,
      note: 'Hold/dependency tracking not available yet.',
      reason: 'dependency_deferred',
    };
  }

  if (bucket === 'diag_plus_work') {
    const remaining =
      input.remainingApprovedWork?.remainingApprovedWorkMinutes ?? null;
    if (remaining === null) {
      return {
        shouldRender: true,
        bucketLabel: input.jobMix.label,
        valueMinutes: null,
        note: 'Remaining approved work unavailable; impact not calculated.',
        reason: 'overlap_unavailable',
      };
    }
    const result = calculateAdvisorDelayImpact({
      advisorObservedMinutes: input.advisorObservedMinutes,
      jobMixBucket: 'diag_plus_work',
      remainingApprovedWorkMinutes: remaining,
    });
    return {
      shouldRender: true,
      bucketLabel: input.jobMix.label,
      valueMinutes: result.advisorDelayImpactMinutes,
      note: 'Adjusted for remaining approved work that could overlap.',
      reason: 'value',
    };
  }

  // diag_only · work_only · comeback_recheck
  const result = calculateAdvisorDelayImpact({
    advisorObservedMinutes: input.advisorObservedMinutes,
    jobMixBucket: bucket,
  });
  let note: string;
  switch (bucket) {
    case 'diag_only':
      note =
        'Inspection/diag-only ticket: advisor time usually blocks next work.';
      break;
    case 'work_only':
      note =
        'Work-only ticket: advisor delay is normally not counted unless a hold/dependency exists.';
      break;
    case 'comeback_recheck':
      note = 'Comeback/recheck: medium advisor communication weight.';
      break;
  }
  return {
    shouldRender: true,
    bucketLabel: input.jobMix.label,
    valueMinutes: result.advisorDelayImpactMinutes,
    note,
    reason: 'value',
  };
}
